/**
 * The platform's semantic colour vocabulary.
 *
 * ONE union, shared by every component that colours something by state
 * (`Badge`, `StatCardGrid`, …). It lives here rather than inside a component
 * so the two apps cannot drift into private forks of the same seven colours —
 * which is exactly how the codebase ended up with 17 separate colour-by-state
 * tables.
 *
 * SEMANTIC, not chromatic: pick the tone by what the state MEANS, never by the
 * colour you want. `warning` because it needs attention, not because it is
 * amber. Raw colour names as an API (`lime`, `teal`, `sky`…) are what the old
 * tenant Badge did, and 9 of its 21 variants were never used once.
 *
 * `purple` is the one non-semantic member and it is deliberate: CLAUDE.md fixes
 * material identity app-wide as filament = amber, resin = purple. Filament's
 * amber is already `warning`; resin had no equivalent.
 *
 * NOT everything belongs here. An ORDINAL ramp (the 5-step order-status
 * progression brown -> brand orange -> logo pink) is a domain scale, not a
 * semantic axis — it composes `badgeBaseClass` with its own colour table
 * instead. See documentation/Standards.md §22.
 */

export type Tone = 'neutral' | 'primary' | 'success' | 'warning' | 'info' | 'danger' | 'purple';

export interface ToneClasses {
    /** Icon colour. */
    icon: string;
    /** Large figure / emphasised text colour. */
    value: string;
    /** Pill text colour — separate from `pillBg` so dense layouts can drop the fill. */
    pillText: string;
    /** Pill fill. */
    pillBg: string;
    /** Border for a bordered pill (Badge). */
    pillBorder: string;
    /** Hover border for an interactive card. */
    hover: string;
    /** Ring for a selected/active card. */
    ring: string;
}

export const toneClasses: Record<Tone, ToneClasses> = {
    neutral: {
        icon: 'text-slate-400',
        value: 'text-slate-700 dark:text-white',
        pillText: 'text-slate-600 dark:text-slate-300',
        pillBg: 'bg-slate-100 dark:bg-surface-hover-dark',
        pillBorder: 'border-slate-200 dark:border-slate-700',
        hover: 'hover:border-primary/30 dark:hover:border-primary/30',
        ring: 'border-slate-400 ring-2 ring-slate-400/40',
    },
    primary: {
        icon: 'text-primary dark:text-primary-light',
        value: 'text-primary dark:text-primary-light',
        pillText: 'text-primary-dark dark:text-primary-light',
        pillBg: 'bg-primary/10 dark:bg-primary/20',
        pillBorder: 'border-primary/20 dark:border-primary/30',
        hover: 'hover:border-primary/50 dark:hover:border-primary/50',
        ring: 'border-primary ring-2 ring-primary/40',
    },
    success: {
        icon: 'text-emerald-500',
        value: 'text-emerald-600 dark:text-emerald-400',
        pillText: 'text-emerald-800 dark:text-emerald-300',
        pillBg: 'bg-emerald-100 dark:bg-emerald-900/40',
        pillBorder: 'border-emerald-200 dark:border-emerald-800',
        hover: 'hover:border-emerald-500/50 dark:hover:border-emerald-500/50',
        ring: 'border-emerald-500 ring-2 ring-emerald-500/40',
    },
    warning: {
        icon: 'text-amber-500',
        value: 'text-amber-600 dark:text-amber-400',
        pillText: 'text-amber-800 dark:text-amber-300',
        pillBg: 'bg-amber-100 dark:bg-amber-900/40',
        pillBorder: 'border-amber-200 dark:border-amber-800',
        hover: 'hover:border-amber-500/50 dark:hover:border-amber-500/50',
        ring: 'border-amber-500 ring-2 ring-amber-500/40',
    },
    info: {
        icon: 'text-blue-500',
        value: 'text-blue-600 dark:text-blue-400',
        pillText: 'text-blue-800 dark:text-blue-300',
        pillBg: 'bg-blue-100 dark:bg-blue-900/40',
        pillBorder: 'border-blue-200 dark:border-blue-800',
        hover: 'hover:border-blue-500/50 dark:hover:border-blue-500/50',
        ring: 'border-blue-500 ring-2 ring-blue-500/40',
    },
    danger: {
        icon: 'text-red-500',
        value: 'text-red-600 dark:text-red-400',
        pillText: 'text-red-700 dark:text-red-400',
        pillBg: 'bg-red-50 dark:bg-red-900/30',
        pillBorder: 'border-red-200 dark:border-red-800',
        hover: 'hover:border-red-500/50 dark:hover:border-red-500/50',
        ring: 'border-red-500 ring-2 ring-red-500/40',
    },
    purple: {
        icon: 'text-purple-500',
        value: 'text-purple-600 dark:text-purple-400',
        pillText: 'text-purple-800 dark:text-purple-300',
        pillBg: 'bg-purple-100 dark:bg-purple-900/40',
        pillBorder: 'border-purple-200 dark:border-purple-800',
        hover: 'hover:border-purple-500/50 dark:hover:border-purple-500/50',
        ring: 'border-purple-500 ring-2 ring-purple-500/40',
    },
};
